import React, { useState, useEffect } from 'react';
import {
  LineChart, Line, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import axios from '../services/axiosConfig';
import config from '../config';
import '../styles/dashboard.css';

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8'];

function Dashboard({ onNavegar }) {
  const [estadisticas, setEstadisticas] = useState({
    turnosHoy: 0,
    pacientesActivos: 0,
    ingresosMes: 0,
    turnosCompletados: 0,
    pacientesNuevos: 0,
    totalTurnosMes: 0,
    turnosCancelados: 0,
    noShows: 0,
    tasaCompletado: 0,
    tasaCancelacion: 0,
    tasaNoShow: 0
  });
  const [ingresosMensuales, setIngresosMensuales] = useState([]);
  const [turnosPorEstado, setTurnosPorEstado] = useState([]);
  const [topPacientes, setTopPacientes] = useState([]);
  const [turnosPorDia, setTurnosPorDia] = useState([]);
  const [turnosHoy, setTurnosHoy] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState(null);

  const cargarDatos = async () => {
    try {
      const token = localStorage.getItem(config.tokenKey);
      const user = localStorage.getItem(config.userKey);
      
      console.log('🔑 Token:', token ? '✅ Existe' : '❌ No existe');
      console.log('👤 Usuario:', user ? '✅ Existe' : '❌ No existe');

      if (!token || !user) {
        console.warn('⚠️ Usuario no autenticado');
        setError('Usuario no autenticado');
        setCargando(false);
        return;
      }

      const [
        estadisticasRes,
        turnosRes,
        ingresosRes,
        estadosRes,
        pacientesRes,
        diasRes
      ] = await Promise.all([
        axios.get('/dashboard/estadisticas'),
        axios.get('/dashboard/turnos-hoy'),
        axios.get('/dashboard/ingresos-mensuales'),
        axios.get('/dashboard/turnos-por-estado'),
        axios.get('/dashboard/top-pacientes'),
        axios.get('/dashboard/turnos-por-dia')
      ]);
      
      setEstadisticas(estadisticasRes.data);
      setTurnosHoy(turnosRes.data);
      
      // Formatear ingresos mensuales
      const ingresosFormateados = ingresosRes.data.map(item => ({
        mes: formatearMes(item.mes),
        total: parseFloat(item.total || 0)
      }));
      setIngresosMensuales(ingresosFormateados);
      
      // Formatear turnos por estado
      const estadosFormateados = estadosRes.data.map(item => ({
        name: capitalizar(item.estado || 'Sin estado'),
        value: parseInt(item.cantidad || 0)
      }));
      setTurnosPorEstado(estadosFormateados);
      
      // Formatear top pacientes
      const pacientesFormateados = pacientesRes.data.map(item => ({
        nombre: item.nombre || 'Desconocido',
        turnos: parseInt(item.total_turnos || 0)
      }));
      setTopPacientes(pacientesFormateados);
      
      // Formatear turnos por día
      setTurnosPorDia(diasRes.data);
      
      setError(null);
    } catch (error) {
      console.error('Error cargando datos:', error);
      setError('Error al cargar los datos');
    } finally {
      setCargando(false);
    }
  };

  // Funciones auxiliares
  const formatearMes = (mesAno) => {
    const [ano, mes] = mesAno.split('-');
    const meses = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];
    return `${meses[parseInt(mes) - 1]} ${ano}`;
  };

  const capitalizar = (str) => {
    return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
  };

  const formatearMoneda = (valor) => {
    return new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS' }).format(valor);
  };

  useEffect(() => {
    cargarDatos();
  }, []);

  const cambiarEstadoTurno = async (turnoId, nuevoEstado) => {
    try {
      await axios.put(`/turnos/${turnoId}/estado`, {
        estado: nuevoEstado
      });
      cargarDatos();
    } catch (error) {
      console.error('Error actualizando turno:', error);
      alert('Error al actualizar el estado del turno');
    }
  };

  const handleAccionRapida = (accion) => {
    if (onNavegar) {
      onNavegar(accion);
    }
  };

  if (cargando) return <div className="loading">Cargando dashboard...</div>;
  if (error) return <div className="error">{error}</div>;

  const usuario = localStorage.getItem(config.userKey) ? JSON.parse(localStorage.getItem(config.userKey)) : null;

  return (
    <div className="dashboard">
      <div className="dashboard-header">
        <div>
          <h1>Bienvenido, {usuario?.nombre || 'Usuario'} 👋</h1>
          <p className="dashboard-subtitle">Resumen de actividad y estadísticas</p>
        </div>
        <button className="btn-refresh" onClick={cargarDatos}>
          🔄 Actualizar
        </button>
      </div>
      
      {/* KPIs Principales */}
      <div className="stats-grid">
        <div className="stat-card primary">
          <div className="stat-icon">📅</div>
          <div className="stat-content">
            <h3>Turnos Hoy</h3>
            <p className="stat-number">{estadisticas.turnosHoy}</p>
          </div>
        </div>
        
        <div className="stat-card success">
          <div className="stat-icon">✅</div>
          <div className="stat-content">
            <h3>Completados (Mes)</h3>
            <p className="stat-number">{estadisticas.turnosCompletados}</p>
            <p className="stat-detail">{estadisticas.tasaCompletado}% del total</p>
          </div>
        </div>
        
        <div className="stat-card info">
          <div className="stat-icon">👥</div>
          <div className="stat-content">
            <h3>Pacientes Activos</h3>
            <p className="stat-number">{estadisticas.pacientesActivos}</p>
          </div>
        </div>
        
        <div className="stat-card accent">
          <div className="stat-icon">🆕</div>
          <div className="stat-content">
            <h3>Pacientes Nuevos</h3>
            <p className="stat-number">{estadisticas.pacientesNuevos}</p>
            <p className="stat-detail">Este mes</p>
          </div>
        </div>
        
        <div className="stat-card money">
          <div className="stat-icon">💰</div>
          <div className="stat-content">
            <h3>Ingresos del Mes</h3>
            <p className="stat-number">{formatearMoneda(estadisticas.ingresosMes)}</p>
          </div>
        </div>
        
        <div className="stat-card warning">
          <div className="stat-icon">📊</div>
          <div className="stat-content">
            <h3>Total Turnos Mes</h3>
            <p className="stat-number">{estadisticas.totalTurnosMes}</p>
          </div>
        </div>
      </div>

      {/* Gráficos */}
      <div className="graficos-grid">
        {/* Gráfico de Ingresos Mensuales */}
        <div className="grafico-card">
          <h3>💰 Ingresos Mensuales (Últimos 6 meses)</h3>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={ingresosMensuales}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="mes" />
              <YAxis />
              <Tooltip formatter={(value) => formatearMoneda(value)} />
              <Legend />
              <Line 
                type="monotone" 
                dataKey="total" 
                stroke="#28a745" 
                strokeWidth={3}
                name="Ingresos"
              />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Gráfico de Turnos por Estado */}
        <div className="grafico-card">
          <h3>📊 Distribución de Turnos (Este mes)</h3>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={turnosPorEstado}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                outerRadius={100}
                fill="#8884d8"
                dataKey="value"
              >
                {turnosPorEstado.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </div>

        {/* Gráfico de Top Pacientes */}
        <div className="grafico-card">
          <h3>🏆 Pacientes Más Frecuentes (Últimos 3 meses)</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={topPacientes}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="nombre" angle={-45} textAnchor="end" height={100} />
              <YAxis />
              <Tooltip />
              <Legend />
              <Bar dataKey="turnos" fill="#007bff" name="Turnos" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Gráfico de Turnos por Día */}
        <div className="grafico-card">
          <h3>📅 Turnos por Día de la Semana (Últimos 30 días)</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={turnosPorDia}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="dia" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Bar dataKey="cantidad" fill="#17a2b8" name="Turnos" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Métricas adicionales */}
      <div className="metricas-section">
        <h3>📈 Métricas del Mes</h3>
        <div className="metricas-grid">
          <div className="metrica-item">
            <span className="metrica-label">Tasa de Asistencia</span>
            <span className="metrica-valor success">{estadisticas.tasaCompletado}%</span>
          </div>
          <div className="metrica-item">
            <span className="metrica-label">Turnos Cancelados</span>
            <span className="metrica-valor warning">{estadisticas.turnosCancelados} ({estadisticas.tasaCancelacion}%)</span>
          </div>
          <div className="metrica-item">
            <span className="metrica-label">No-Shows</span>
            <span className="metrica-valor danger">{estadisticas.noShows} ({estadisticas.tasaNoShow}%)</span>
          </div>
        </div>
      </div>

      {/* Turnos de Hoy */}
      <div className="turnos-section">
        <h3>📅 Turnos de Hoy</h3>
        {turnosHoy.length === 0 ? (
          <p className="no-turnos">No hay turnos programados para hoy.</p>
        ) : (
          <div className="turnos-list">
            {turnosHoy.map(turno => (
              <div key={turno.id} className="turno-item">
                <div className="turno-info-dashboard">
                  <h4>{turno.paciente_nombre || 'Sin paciente'}</h4>
                  <p>⏰ {new Date(turno.fecha_hora).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})} - {turno.duracion} min</p>
                </div>
                <span className={`turno-estado ${turno.estado}`}>{turno.estado}</span>
                <div className="turno-acciones-dashboard">
                  {turno.estado === 'pendiente' && (
                    <>
                      <button 
                        className="btn-turno completar"
                        onClick={() => cambiarEstadoTurno(turno.id, 'completado')}
                        title="Marcar como completado"
                      >
                        ✅ Completar
                      </button>
                      <button 
                        className="btn-turno cancelar"
                        onClick={() => cambiarEstadoTurno(turno.id, 'cancelado')}
                        title="Cancelar turno"
                      >
                        ❌ Cancelar
                      </button>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Acciones Rápidas */}
      <div className="acciones-rapidas">
        <div className="accion-card" onClick={() => handleAccionRapida('pacientes')}>
          <div className="icono">👤</div>
          <h4>Nuevo Paciente</h4>
        </div>
        <div className="accion-card" onClick={() => handleAccionRapida('turnos')}>
          <div className="icono">📅</div>
          <h4>Nuevo Turno</h4>
        </div>
        <div className="accion-card" onClick={() => handleAccionRapida('pagos')}>
          <div className="icono">💰</div>
          <h4>Registrar Pago</h4>
        </div>
      </div>
    </div>
  );
}

export default Dashboard;
